import { GEO_NO_DEAL_ERROR, softwareErrorsFrom, type Job } from "@domain/CRM/job";
import { runPooled } from "@domain/Utilities";

export interface JobErrorsUseCasesDeps {
	/** Waits for the geo-processing Lambda, with validation enabled. */
	processGeo(jobId: string): Promise<void>;
}

interface RevalidateResolvedDealsInput {
	jobs: readonly Job[];
	/** Owned by the page visit, never by the singleton use case. Includes failed attempts. */
	attemptedIds: Set<string>;
	onRunningChange: (jobId: string, running: boolean) => void;
}

/** How far a batch has got with one job. */
export type JobBatchState = "queued" | "running" | "done" | "failed" | "skipped";

/** How many of a batch's actions run at once — headroom in the API's shared 40-thread pool beside automatic
 *  revalidation's 20. */
export const JOB_ERROR_BATCH_CONCURRENCY = 4;

export interface RunJobErrorBatchInput {
	jobIds: readonly string[];
	/** The tab's action for one job; resolves once the work is done. */
	perform: (jobId: string) => Promise<unknown>;
	/** Whether the job is already being worked on elsewhere. Asked as each job's turn comes, not only at the start. */
	isBusy: (jobId: string) => boolean;
	onProgress: (jobId: string, state: JobBatchState, error?: unknown) => void;
	concurrency?: number;
}

interface JobErrorBatchResult {
	succeeded: string[];
	failed: { jobId: string; error: unknown }[];
	skipped: string[];
}

export interface JobErrorsUseCases {
	revalidateResolvedDeals(input: RevalidateResolvedDealsInput): Promise<{ attempted: number; failedIds: string[] }>;
	runBatch(input: RunJobErrorBatchInput): Promise<JobErrorBatchResult>;
}

/** Retry stale missing-deal errors in a small queue, without blocking other jobs on a failed run. */
async function revalidateResolvedDeals(
	deps: JobErrorsUseCasesDeps,
	{ jobs, attemptedIds, onRunningChange }: RevalidateResolvedDealsInput,
): Promise<{ attempted: number; failedIds: string[] }> {
	const candidates = jobs.filter((job) => {
		const eligible =
			job.dealIds?.some((id) => id.trim() !== "" && id !== "custom") &&
			softwareErrorsFrom(job, "Geo-Spatial Validator").some((error) => error.note === GEO_NO_DEAL_ERROR);
		if (!eligible || attemptedIds.has(job.id)) return false;
		// Claim synchronously, before any await: duplicate rows and overlapping reloads cannot retry it.
		attemptedIds.add(job.id);
		return true;
	});
	const failedIds: string[] = [];
	let next = 0;
	const worker = async () => {
		while (next < candidates.length) {
			const job = candidates[next++];
			onRunningChange(job.id, true);
			try {
				await deps.processGeo(job.id);
			} catch {
				failedIds.push(job.id);
			} finally {
				onRunningChange(job.id, false);
			}
		}
	};
	// Leave headroom in the API's shared 40-thread pool for ordinary Portal requests.
	await Promise.all(Array.from({ length: Math.min(20, candidates.length) }, worker));
	return { attempted: candidates.length, failedIds };
}

/** Run a tab's action over the selected jobs a few at a time, skipping any already being worked on. A failed job
 *  doesn't stop the rest; every job's progress is reported as it goes. */
async function runJobErrorBatch({
	jobIds,
	perform,
	isBusy,
	onProgress,
	concurrency = JOB_ERROR_BATCH_CONCURRENCY,
}: RunJobErrorBatchInput): Promise<JobErrorBatchResult> {
	for (const id of jobIds) onProgress(id, "queued");
	const result: JobErrorBatchResult = { succeeded: [], failed: [], skipped: [] };
	await runPooled(jobIds, concurrency, async (id) => {
		if (isBusy(id)) {
			result.skipped.push(id);
			onProgress(id, "skipped");
			return;
		}
		onProgress(id, "running");
		try {
			await perform(id);
		} catch (error) {
			result.failed.push({ jobId: id, error });
			onProgress(id, "failed", error);
			return;
		}
		result.succeeded.push(id);
		onProgress(id, "done");
	});
	const order = new Map(jobIds.map((id, i) => [id, i]));
	const byOrder = (a: string, b: string) => order.get(a)! - order.get(b)!;
	result.succeeded.sort(byOrder);
	result.skipped.sort(byOrder);
	result.failed.sort((a, b) => byOrder(a.jobId, b.jobId));
	return result;
}

export function makeJobErrorsUseCases(deps: JobErrorsUseCasesDeps): JobErrorsUseCases {
	return {
		revalidateResolvedDeals: (input: RevalidateResolvedDealsInput) => revalidateResolvedDeals(deps, input),
		runBatch: (input: RunJobErrorBatchInput) => runJobErrorBatch(input),
	};
}
